import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { CircleQuestionMark, FileText, MessageCircle, Video } from 'lucide-react';
import { useTheme } from '@course-authoring/shared-types';
import { buildFileUrl, isUploadedFileSource, type BuildListItem, type BuildSource, type BuildStatus } from '../../api/authorWithAiApi';
import { useBuildList } from '../../api/authorWithAiQueries';
import { BADGES, LANDING_COPY, ORIGIN_FILE_LABEL, ORIGIN_LABELS } from './authorWithAiCopy';
import { createAuthorWithAiStyles } from './authorWithAiStyles';

/**
 * The landing page's three lists for the author's non-discarded builds in
 * the current content context, four rows at a time with Show more.
 */

const IN_PROGRESS: readonly BuildStatus[] = ['building'];
const RECENT: readonly BuildStatus[] = ['completed'];
const ALL: readonly BuildStatus[] = [];

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/** "Just now", "20 minutes ago", "2 hours ago", "Yesterday", then "Sep 18". */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const minutes = Math.round((now.getTime() - then.getTime()) / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return capitalize(RELATIVE.format(-minutes, 'minute'));
  const hours = Math.round(minutes / 60);
  if (hours < 24) return capitalize(RELATIVE.format(-hours, 'hour'));
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.ceil((startOfToday.getTime() - then.getTime()) / 86_400_000);
  if (days <= 1) return capitalize(RELATIVE.format(-1, 'day'));
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function OriginIcon({ source, buildId }: { source: BuildSource; buildId: string }) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
  if (isUploadedFileSource(source)) {
    // The file stays with the build, so the glyph is its download.
    return (
      <a href={buildFileUrl(buildId)} download={source.fileName} style={styles.origin} aria-label={ORIGIN_FILE_LABEL(source.fileName)} title={source.fileName}>
        <FileText size="1em" strokeWidth={1.75} aria-hidden="true" />
      </a>
    );
  }
  const kind = source.kind === 'question' || source.kind === 'question_browse' || source.kind === 'video' || source.kind === 'video_browse'
    ? source.kind
    : 'none';
  const Icon = kind === 'question' || kind === 'question_browse' ? CircleQuestionMark : kind === 'none' ? MessageCircle : Video;
  return (
    <span style={styles.origin} role="img" aria-label={ORIGIN_LABELS[kind]} title={ORIGIN_LABELS[kind]}>
      <Icon size="1em" strokeWidth={1.75} aria-hidden="true" />
    </span>
  );
}

const conversationPath = (buildId: string) => `/author-with-ai/builds/${buildId}`;

type RowVariant = 'inProgress' | 'recent' | 'conversations';

function BuildRow({ item, variant, styles }: { item: BuildListItem; variant: RowVariant; styles: Record<string, CSSProperties> }) {
  const badge = (label: string, extra: CSSProperties = {}, spinner = false) => (
    <span style={{ ...styles.badge, ...extra }}>
      {spinner && <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />}
      {label}
    </span>
  );
  const outputId = item.output && (
    <Link to={item.output.editorPath} style={styles.outputId} aria-label={`Open ${item.output.displayId} in the editor`}>
      {item.output.displayId}
    </Link>
  );
  let trailing: ReactNode = null;
  if (variant === 'inProgress') {
    trailing = (
      <>
        {badge(BADGES.building, styles.badgeBuilding, true)}
        {item.progress && <span style={styles.when}>{`${item.progress.ready} of ${item.progress.total} lessons`}</span>}
      </>
    );
  } else {
    const status = variant === 'recent'
      ? item.output && badge(item.output.publishState === 'published' ? BADGES.published : BADGES.draft,
        item.output.publishState === 'published' ? styles.badgePublished : styles.badgeDraft)
      : item.status === 'completed' ? outputId
        : item.status === 'failed' ? badge(BADGES.failed, styles.badgeFailed)
          : item.status === 'building' ? badge(BADGES.building, styles.badgeBuilding, true)
            : badge(BADGES.unfinished);
    trailing = (
      <>
        {status}
        <span style={styles.when}>{formatRelativeTime(item.updatedAt)}</span>
      </>
    );
  }
  return (
    <li style={styles.row} data-testid={`build-row-${item.id}`}>
      <OriginIcon source={item.source} buildId={item.id} />
      {variant === 'recent' && outputId}
      <Link to={conversationPath(item.id)} style={styles.rowTitle}>{item.title}</Link>
      {trailing}
    </li>
  );
}

function BuildListSection({
  heading,
  accountId,
  statuses,
  variant,
  hideWhenEmpty,
  emptyText,
  styles,
}: {
  heading: string;
  accountId: number | null;
  statuses: readonly BuildStatus[];
  variant: RowVariant;
  hideWhenEmpty?: boolean;
  emptyText?: string;
  styles: Record<string, CSSProperties>;
}) {
  const query = useBuildList(accountId, statuses);
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  if (hideWhenEmpty && items.length === 0) return null;
  const headingId = `author-with-ai-list-${variant}`;
  return (
    <section style={styles.listSection} aria-labelledby={headingId}>
      <h3 id={headingId} style={styles.subheading}>{heading}</h3>
      {query.isError && <p style={styles.error}>{LANDING_COPY.listError}</p>}
      {query.isSuccess && items.length === 0 && emptyText && <p style={styles.muted}>{emptyText}</p>}
      {items.length > 0 && (
        <ul style={styles.list}>
          {items.map((item) => <BuildRow key={item.id} item={item} variant={variant} styles={styles} />)}
        </ul>
      )}
      {query.hasNextPage && (
        <button type="button" style={styles.showMore} onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage}>
          {LANDING_COPY.showMore}
        </button>
      )}
    </section>
  );
}

export function BuildLists({ accountId }: { accountId: number | null }) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
  return (
    <div style={styles.lists}>
      <BuildListSection heading={LANDING_COPY.inProgress} accountId={accountId} statuses={IN_PROGRESS} variant="inProgress" hideWhenEmpty styles={styles} />
      <BuildListSection heading={LANDING_COPY.recent} accountId={accountId} statuses={RECENT} variant="recent" emptyText={LANDING_COPY.emptyRecent} styles={styles} />
      <BuildListSection heading={LANDING_COPY.conversations} accountId={accountId} statuses={ALL} variant="conversations" emptyText={LANDING_COPY.emptyConversations} styles={styles} />
    </div>
  );
}
